/**
 * Product pricing summary runtime.
 *
 * Hydrates the PDP payment card from the same finance configuration used by
 * the monthly-payment calculator while owning its request and section lifecycle.
 */

(() => {
  'use strict';

  const RUNTIME_KEY = '__MarineMaxProductPricingSummary';
  const INSTANCE_KEY = '__productPricingSummaryInstance';
  const ROOT_SELECTOR = '[data-product-pricing-summary]';
  const CONFIG_SELECTOR = '#mm-pricing-summary-config';
  const PAYMENT_CARD_SELECTOR = '[data-payment-card]';
  const PAYMENT_AMOUNT_SELECTOR = '[data-payment-amount]';
  const OUT_OF_RANGE_CLASS = 'mm-payment-out-of-range';
  const DEFAULT_RATE_ENDPOINT =
    'https://integrate.marinemax.com/finance/GetRate';
  const DEFAULT_FALLBACK_APR = 8.5;
  const DEFAULT_PAYMENT_FLOOR = 200;
  const DEFAULT_PAYMENT_CEILING = 5000;
  const DEFAULT_DOWN_PAYMENT_PERCENT = 20;
  const DEFAULT_LOAN_TERM_YEARS = 20;
  const DEFAULT_FICO_SCORE = 800;
  const instances = new Set();
  const calculatorOutOfRangeOwners = new Set();
  const outOfRangeOwners = new Set();
  const usdFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const existingRuntime = window[RUNTIME_KEY];
  if (existingRuntime?.initialize) {
    existingRuntime.initialize(document);
    return;
  }

  function parseInteger(value, fallback = 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseFloatValue(value, fallback) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseMonthlyPayment(value) {
    if (value == null || value === '') return null;

    const parsed = Number.parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function formatUsdNoCents(amount) {
    return usdFormatter.format(amount);
  }

  function calculateAmortizedPayment(
    principalDollars,
    annualRatePercent,
    termMonths,
  ) {
    if (
      !principalDollars ||
      principalDollars <= 0 ||
      !termMonths ||
      termMonths <= 0
    ) {
      return null;
    }

    const monthlyRate = annualRatePercent / 100 / 12;
    if (monthlyRate === 0) return principalDollars / termMonths;

    const compound = Math.pow(1 + monthlyRate, termMonths);
    return (
      (principalDollars * (monthlyRate * compound)) / (compound - 1)
    );
  }

  function readSharedConfig() {
    const node = document.querySelector(CONFIG_SELECTOR);
    if (!node) return {};

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (_) {
      return {};
    }
  }

  function resolveRateEndpoint(value) {
    return value || DEFAULT_RATE_ENDPOINT;
  }

  function calculatorReportsOutOfRange() {
    return Array.from(
      document.querySelectorAll('[data-monthly-payment-calculator]'),
    ).some((calculator) => {
      const message = calculator.querySelector('[data-mm-calculator-oor]');
      return message && !message.hasAttribute('hidden');
    });
  }

  function syncPaymentOutOfRangeState() {
    document.body?.classList.toggle(
      OUT_OF_RANGE_CLASS,
      outOfRangeOwners.size > 0 ||
        calculatorOutOfRangeOwners.size > 0 ||
        calculatorReportsOutOfRange(),
    );

    const configNode = document.querySelector(CONFIG_SELECTOR);
    const calculatorSection = configNode?.closest?.('[id^="shopify-section"]');
    calculatorSection?.toggleAttribute('hidden', outOfRangeOwners.size > 0);
  }

  function setPaymentOutOfRangeState(owner, active) {
    if (active) outOfRangeOwners.add(owner);
    else outOfRangeOwners.delete(owner);
    owner.isPaymentOutOfRange = active;
    syncPaymentOutOfRangeState();
  }

  function setCalculatorOutOfRangeState(owner, active) {
    if (!owner) return;
    if (active) calculatorOutOfRangeOwners.add(owner);
    else calculatorOutOfRangeOwners.delete(owner);
    syncPaymentOutOfRangeState();
  }

  function resolveMonthlyPayment(data, loanAmountDollars, termMonths) {
    const apiSuccess =
      data && (data.ResultCode === 'F' || data.ResultCode === 'f');

    if (apiSuccess) {
      const apiPayment =
        data.MonthlyPayment || data.monthlyPayment || data.monthly_payment;
      const parsedPayment = parseMonthlyPayment(apiPayment);
      if (parsedPayment != null) return parsedPayment;
    }

    const rawRate = data && (data.Rate || data.rate);
    let annualRate = rawRate
      ? Number.parseFloat(rawRate)
      : DEFAULT_FALLBACK_APR;
    if (!Number.isFinite(annualRate) || annualRate <= 0) {
      annualRate = DEFAULT_FALLBACK_APR;
    }

    return calculateAmortizedPayment(
      loanAmountDollars,
      annualRate,
      termMonths,
    );
  }

  class ProductPricingSummary {
    constructor(root) {
      if (!root) return;
      if (root[INSTANCE_KEY]) return root[INSTANCE_KEY];

      this.root = root;
      this.elements = {
        amount: root.querySelector(PAYMENT_AMOUNT_SELECTOR),
        paymentCard: root.querySelector(PAYMENT_CARD_SELECTOR),
      };
      if (!this.elements.amount || !this.elements.paymentCard) return;

      this.requestAbortController = null;
      this.hydrationVersion = 0;
      this.isDestroyed = false;
      this.isPaymentOutOfRange = false;
      root[INSTANCE_KEY] = this;
      instances.add(this);
      this.hydrationPromise = this.hydrate();
    }

    readConfiguration() {
      const sharedConfig = readSharedConfig();

      return {
        ceiling: parseFloatValue(
          sharedConfig.ceiling,
          parseFloatValue(
            this.root.getAttribute('data-ceiling'),
            DEFAULT_PAYMENT_CEILING,
          ),
        ),
        downPaymentPercent: parseInteger(
          sharedConfig.downPaymentDefault,
          DEFAULT_DOWN_PAYMENT_PERCENT,
        ),
        ficoScore: parseInteger(
          sharedConfig.creditRatingDefault,
          DEFAULT_FICO_SCORE,
        ),
        floor: parseFloatValue(
          sharedConfig.floor,
          parseFloatValue(
            this.root.getAttribute('data-floor'),
            DEFAULT_PAYMENT_FLOOR,
          ),
        ),
        loanTermYears: parseInteger(
          sharedConfig.loanDurationDefault,
          DEFAULT_LOAN_TERM_YEARS,
        ),
        rateEndpoint: resolveRateEndpoint(sharedConfig.rateEndpoint),
      };
    }

    buildCalculation() {
      const priceCents = parseInteger(
        this.root.getAttribute('data-price-cents'),
        0,
      );
      if (priceCents <= 0) return null;

      const configuration = this.readConfiguration();
      const priceDollars = priceCents / 100;
      const loanAmountDollars = Math.round(
        priceDollars * (1 - configuration.downPaymentPercent / 100),
      );
      const termMonths = configuration.loanTermYears * 12;

      return {
        configuration,
        loanAmountDollars,
        requestPayload: {
          ficoScore: String(configuration.ficoScore),
          percentDown: configuration.downPaymentPercent,
          term: termMonths,
          loanAmount: loanAmountDollars,
        },
        termMonths,
      };
    }

    async fetchRate(endpoint, payload, signal) {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal,
        });
        if (!response.ok) return null;
        return await response.json();
      } catch (_) {
        return null;
      }
    }

    showInvalidPaymentState() {
      this.elements.paymentCard.removeAttribute('data-loading');
      this.elements.paymentCard.setAttribute('hidden', '');
      this.root.removeAttribute('data-payment-out-of-range');
      this.root.removeAttribute('hidden');
      setPaymentOutOfRangeState(this, false);
    }

    showOutOfRangeState() {
      this.elements.paymentCard.removeAttribute('data-loading');
      this.elements.paymentCard.setAttribute('hidden', '');
      this.root.setAttribute('data-payment-out-of-range', 'true');
      setPaymentOutOfRangeState(this, true);

      if (this.root.getAttribute('data-has-discount') !== 'true') {
        this.root.setAttribute('hidden', '');
      }
    }

    showPayment(monthlyPayment) {
      this.elements.amount.textContent = formatUsdNoCents(monthlyPayment);
      this.elements.paymentCard.removeAttribute('data-loading');
      this.elements.paymentCard.removeAttribute('hidden');
      this.root.removeAttribute('data-payment-out-of-range');
      this.root.removeAttribute('hidden');
      setPaymentOutOfRangeState(this, false);
    }

    async hydrate() {
      const hydrationVersion = ++this.hydrationVersion;
      this.requestAbortController?.abort();

      const calculation = this.buildCalculation();
      this.elements.paymentCard.setAttribute('data-loading', 'true');

      if (!calculation) {
        this.elements.paymentCard.removeAttribute('data-loading');
        this.elements.paymentCard.setAttribute('hidden', '');
        this.root.removeAttribute('data-payment-out-of-range');
        this.root.removeAttribute('hidden');
        setPaymentOutOfRangeState(this, false);
        return { completed: true, state: 'unavailable' };
      }

      const requestAbortController = new AbortController();
      this.requestAbortController = requestAbortController;
      const data = await this.fetchRate(
        calculation.configuration.rateEndpoint,
        calculation.requestPayload,
        requestAbortController.signal,
      );

      if (
        this.isDestroyed ||
        requestAbortController.signal.aborted ||
        hydrationVersion !== this.hydrationVersion
      ) {
        return { completed: false, state: 'stale' };
      }
      if (isSupersededRoot(this.root)) {
        this.destroy();
        return { completed: false, state: 'stale' };
      }
      if (this.requestAbortController === requestAbortController) {
        this.requestAbortController = null;
      }

      const monthlyPayment = resolveMonthlyPayment(
        data,
        calculation.loanAmountDollars,
        calculation.termMonths,
      );
      if (
        monthlyPayment == null ||
        !Number.isFinite(monthlyPayment) ||
        monthlyPayment <= 0
      ) {
        this.showInvalidPaymentState();
        return { completed: true, state: 'invalid' };
      }

      const { ceiling, floor } = calculation.configuration;
      if (monthlyPayment < floor || monthlyPayment > ceiling) {
        this.showOutOfRangeState();
        return { completed: true, state: 'out-of-range' };
      }

      this.showPayment(monthlyPayment);
      return { completed: true, state: 'ready' };
    }

    destroy() {
      if (this.isDestroyed) return;

      this.isDestroyed = true;
      this.hydrationVersion += 1;
      this.requestAbortController?.abort();
      this.requestAbortController = null;
      instances.delete(this);
      if (this.isPaymentOutOfRange) {
        setPaymentOutOfRangeState(this, false);
      }

      if (this.root?.[INSTANCE_KEY] === this) {
        delete this.root[INSTANCE_KEY];
      }
    }
  }

  function collectRoots(root = document) {
    if (!root) return [];

    const roots = [];
    if (typeof root.matches === 'function' && root.matches(ROOT_SELECTOR)) {
      roots.push(root);
    }
    if (typeof root.querySelectorAll === 'function') {
      roots.push(...root.querySelectorAll(ROOT_SELECTOR));
    }
    return [...new Set(roots)];
  }

  function isSupersededRoot(root) {
    if (root.isConnected === false) return true;
    return Boolean(
      root.closest?.(
        'main[style*="display: none"], product-info[style*="display: none"]',
      ),
    );
  }

  function pruneSupersededInstances() {
    instances.forEach((instance) => {
      if (isSupersededRoot(instance.root)) instance.destroy();
    });
  }

  function initialize(root = document) {
    pruneSupersededInstances();

    return collectRoots(root)
      .filter((summaryRoot) => !isSupersededRoot(summaryRoot))
      .map(
        (summaryRoot) =>
          summaryRoot[INSTANCE_KEY] || new ProductPricingSummary(summaryRoot),
      )
      .filter((instance) => instance?.root?.[INSTANCE_KEY] === instance);
  }

  function destroy(root) {
    collectRoots(root).forEach((summaryRoot) => {
      summaryRoot[INSTANCE_KEY]?.destroy?.();
    });
  }

  function scopeContainsConfiguration(root) {
    if (!root) return false;
    if (typeof root.matches === 'function' && root.matches(CONFIG_SELECTOR)) {
      return true;
    }
    return Boolean(root.querySelector?.(CONFIG_SELECTOR));
  }

  function handleSectionLoad(event) {
    initialize(event.target);

    if (!scopeContainsConfiguration(event.target)) return;
    initialize(document).forEach((instance) => {
      instance.hydrationPromise = instance.hydrate();
    });
  }

  const runtime = Object.freeze({
    ProductPricingSummary,
    destroy,
    initialize,
    setCalculatorOutOfRangeState,
    utilities: Object.freeze({
      calculateAmortizedPayment,
      formatUsdNoCents,
      parseMonthlyPayment,
      resolveMonthlyPayment,
    }),
  });

  Object.defineProperty(window, RUNTIME_KEY, {
    configurable: true,
    enumerable: false,
    value: runtime,
    writable: false,
  });

  if (typeof MutationObserver === 'function' && document.documentElement) {
    const removalObserver = new MutationObserver((records) => {
      if (!records.some((record) => record.removedNodes.length > 0)) return;
      pruneSupersededInstances();
    });
    removalObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(document),
      { once: true },
    );
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', (event) =>
    destroy(event.target),
  );
})();
